import { observable, action, computed } from "mobx";
import EnvironmentUtils from "src/utils/EnvironmentUtils";

export class MultiSearchFormStore {
  frameworkType;
  query;

  // Query params
  @observable filtersOrder = []; // Array of tids
  @observable filtersState = {};

  // Result
  @observable allowedFilters = [];

  constructor(queryCallback) {
    this.query = queryCallback;
  }

  @action
  resetForm() {
    this.filtersOrder = []; // Array of tids
    this.filtersState = {};
  }

  @action
  resetAllowedFilters() {
    this.allowedFilters = [];
  }

  @computed
  get optionsForSelector() {
    return this.allowedFilters.map(({ variable }) => ({
      name: variable.name,
      code: variable.code,
      description: variable.description,
      tid: variable.tid,
      type: variable.type,
      nodes: variable.nodes,
      maintenanceAgencyId: variable?.properties?.MAINTENANCE_AGENCY_ID,
    }));
  }

  @computed
  get optionsForSelectorUnselected() {
    return this.optionsForSelector.filter(
      (item) => !this.filtersOrder.includes(item.tid) || item?.nodes?.length > 0
    );
  }

  @computed
  get allowedFiltersMap() {
    const allowedFiltersMap = {};

    const addSubvariables = (nodes) => {
      for (const node of nodes) {
        allowedFiltersMap[node.tid] = { variable: node };
        if (node.nodes && Array.isArray(node.nodes) && node.nodes.length > 0) {
          addSubvariables(node.nodes);
        }
      }
    };

    for (const filter of this.allowedFilters) {
      allowedFiltersMap[filter.variable.tid] = filter;
      if (
        filter.variable.nodes &&
        Array.isArray(filter.variable.nodes) &&
        filter.variable.nodes.length > 0
      ) {
        addSubvariables(filter.variable.nodes);
      }
    }
    return allowedFiltersMap;
  }

  get allowedOptionsMap() {
    const allowedOptionsMap = {};
    for (const filter of this.allowedFilters) {
      if (!allowedOptionsMap[filter.variable.tid]) {
        allowedOptionsMap[filter.variable.tid] = {};
        if (filter.options && filter.options.length > 0) {
          for (const option of filter.options) {
            allowedOptionsMap[filter.variable.tid][option.id] = option;
          }
        }
      }
    }
    return allowedOptionsMap;
  }

  isEnumeratedFilterItem = (variableId) => {
    return (
      this.allowedOptionsForVariableIds[variableId] &&
      this.allowedOptionsForVariableIds[variableId].length
    );
  };

  /**
   * Adds the TID of the filter (variable) to the order list and for the last TID in the provided items array, creates a new filter state.
   * @param {*} items An array with the TID the of selected filters.
   */
  @action
  addFilterItem = (items) => {
    this.filtersOrder = items;
    const newTid = parseInt(items[items.length - 1], 10);
    this.filtersState[newTid] = {
      variable: this.allowedFiltersMap[newTid]?.variable,
      operator: "OR",
      options: this.allowedFiltersMap[newTid]?.options ? [] : undefined,
    };
    this.query();
  };

  @action
  addFilterItemsBulk = (filterTids, executeQuery = false) => {
    if (!filterTids) {
      return;
    }

    this.filtersOrder = filterTids;
    for (const filterTid of filterTids) {
      const parsedTid = parseInt(filterTid, 10);
      this.filtersState[parsedTid] = {
        variable: this.allowedFiltersMap[parsedTid].variable,
        operator: "OR",
        options: this.allowedFiltersMap[parsedTid].options ? [] : undefined,
      };
    }

    if (executeQuery) {
      this.query();
    }
  };

  @action
  deleteFilterItem = (variableTid) => {
    const orderIndex = this.filtersOrder.findIndex(
      (tid) => tid === variableTid
    );
    this.filtersOrder = [
      ...this.filtersOrder.slice(0, orderIndex),
      ...this.filtersOrder.slice(orderIndex + 1),
    ];
    delete this.filtersState[variableTid];

    this.query();
  };

  @action
  changeFilterOrder = (items) => {
    this.filtersOrder = items;
  };

  @action
  updateFilterOptions = (
    enumeratedListWithConfig,
    variableTid,
    executeQuery = true
  ) => {
    this.filtersState[variableTid].options = enumeratedListWithConfig
      .map(({ objectId, isIgnored, mustMatch }) => {
        const option = this.allowedOptionsMap[variableTid][objectId];
        if (option) {
          if (!option.properties) {
            option.properties = new Map();
          }
          option.properties.set("SHOULD_BE_IGNORED", isIgnored);
          option.properties.set("MUST_MATCH", mustMatch);
        }
        return option;
      })
      .filter((option) => !!option);

    if (executeQuery) {
      this.query();
    }
  };

  @computed
  get queryParams() {
    return {
      filtersOperator: EnvironmentUtils.isSddApp() ? "OR" : "AND",
      filters: this.filtersOrder.map((tid) => this.filtersState[tid]),
    };
  }

  @action
  processAllowedFilters = (allowedFilters) => {
    // filters are combined for MICRO and MACRO world
    this.allowedFilters = allowedFilters;
  };
}
